/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { animated, useTransition } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import React, { Fragment, FunctionComponent } from 'react';
import styled from 'styled-components';

import { SearchFacet } from '@toucan/common/services/search/constants';

import { Filter } from '@toucan/client/features/search/components/model-search-results/types';

import { FilterAccordion } from './components/filter-accordion';
import { FilterHeader } from './components/header';
import { useSelectedItems } from './hooks/use-selected-items';
import { styles } from './search-filter-group.styles';

export type SearchFilterGroupProps = {
    isOpen: boolean;
    filters: Filter[];
    resultsCount: number;
    'data-testid'?: string;
    className?: string;
    isLoadingFilters: boolean;
    onFilterOpen?: (id: SearchFacet) => Promise<void> | void;
    onMenuClose: () => void;
};

const StyledSearchFilterGroup = styled(animated.aside).withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const SearchFilterGroup: FunctionComponent<SearchFilterGroupProps> = ({
    isOpen,
    filters,
    resultsCount,
    onFilterOpen,
    onMenuClose,
    isLoadingFilters,
    ...otherProps
}: SearchFilterGroupProps) => {
    const { t } = useTranslation();

    const {
        selectedFilters,
        hasSelectedFilters,
        handleFilterUpdate,
        handleFilterClear,
        handleApply,
        onResetAndClose,
        handleClearAll,
    } = useSelectedItems(filters, onMenuClose);

    const transitions = useTransition(isOpen, {
        from: {
            transform: 'translateY(5rem)',
            opacity: 0,
        },
        enter: {
            transform: 'translateY(0)',
            opacity: 1,
        },
        leave: {
            transform: 'translateY(5rem)',
            opacity: 0,
        },
    });

    return (
        <Fragment>
            {transitions((props, item) =>
                item ? (
                    <StyledSearchFilterGroup style={props} {...otherProps}>
                        <div className="filter-wrapper">
                            <FilterHeader
                                resultsCount={resultsCount}
                                hasSelectedFilters={hasSelectedFilters}
                                handleClearAll={handleClearAll}
                                onResetAndClose={onResetAndClose}
                            />
                            <div className="filter-content">
                                <FilterAccordion
                                    data-testid="filters-modal__accordion"
                                    onFilterOpen={onFilterOpen}
                                    selectedFilters={selectedFilters}
                                    handleFilterClear={handleFilterClear}
                                    handleFilterUpdate={handleFilterUpdate}
                                    filters={filters}
                                    isLoadingFilters={isLoadingFilters}
                                />
                                <div className="filter-buttons">
                                    <Button
                                        variant={ButtonVariant.TERTIARY}
                                        size={ButtonSize.SMALL}
                                        className="filter-button"
                                        onClick={onResetAndClose}
                                        data-testid="mobile-search-filter__cancel"
                                    >
                                        {t('Cancel')}
                                    </Button>
                                    <Button
                                        className="filter-button"
                                        size={ButtonSize.SMALL}
                                        onClick={handleApply}
                                        data-testid="mobile-search-filter__apply"
                                    >
                                        {t('Apply')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </StyledSearchFilterGroup>
                ) : null
            )}
        </Fragment>
    );
};
